import { createPortal } from 'preact/compat';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { useProductSlider } from "@/hooks/useProductSlider";
import ProductCard from "@/components/ProductCard";
import ProductCardLoading from "@/components/ProductCardLoading";
import PrevIcon from "@/icons/product-slider-left.svg";
import NextIcon from "@/icons/product-slider-right.svg";

/**
 * ProductSlider component.
 *
 * @component
 *
 * @param {Object} props - Component props.
 * @param {Object} props.settings - Settings for the product slider.
 * @param {number} props.settings.index - The index of the slider.
 * @param {string} props.settings.title - The title of the slider.
 * @param {string} props.settings.description - The description of the slider.
 * @param {boolean} [props.settings.portal=true] - Whether to use a portal for the slider.
 *
 * @returns {React.Element} The rendered ProductSlider component.
 */
function ProductSlider({ settings }) {
	const { index, title, description, brand, portal = true, noProductsFoundMessage = null, special} = settings;

	let { products, status, loading, retailer, sliderRef, inViewport, prevBtnRef, nextBtnRef, loopEnabled } = useProductSlider(settings);
	let target = document.querySelector(`#slider-component-root-${index}`);
	let module = document.querySelector(`#module-${index}`);

	if ( portal && module && status === 'complete' && ! products.length ) {
		module.classList.add('hidden');
		return null;
	} else if (module) {
		module.classList.remove('hidden');
	}

	if ( portal && ! target ) {
		return null;
	}

	if ( status === 'complete' && ! products.length ) {
		return null;
	}

	if ( special && products ) {
		products = products.map(product => ({ ...product, coupon: special.id, promotion_id: special.id }));
	}

	const content = (
		<>
		{ title || description ? (
			<div className="product-slider-header">
				<h2 className="title">{title}</h2>
				<p className="description">{description}</p>
			</div>
		) : null }
		<div className="product-slider-contents" ref={sliderRef}>
			<Swiper
				loop={loopEnabled}
				slidesPerView={1.4}
				spaceBetween={8}
				centeredSlides={true}
				centeredSlidesBounds={true}
				modules={[Navigation]}
				watchSlidesProgress={true}
				preventClicks={true}
				navigation={{
					nextEl: nextBtnRef.current,
					prevEl: prevBtnRef.current,
				}}
				onBeforeInit={(swiper) => {
					swiper.params.navigation.prevEl = prevBtnRef.current;
					swiper.params.navigation.nextEl = nextBtnRef.current;
				}}
				breakpoints={{
					576: {
						slidesPerView: 2.2,
						centeredSlides: false,
					},
					992: {
						spaceBetween: 12,
						slidesPerView: 3.2,
						centeredSlides: false,
					},
					1200: {
						spaceBetween: 16,
						slidesPerView: 4,
						centeredSlides: false,
					},
				}}
			>
				{loading ? (
				Array(6)
					.fill()
					.map((item, index) => (
						<SwiperSlide key={index}>
							<ProductCardLoading />
						</SwiperSlide>
					))
				) : null}

				{products && products.length
					? products.map((product, index) => (
						<SwiperSlide key={product.id}>
							<ProductCard product={product} retailer={retailer}>
								<ProductCard.Header>
									<ProductCard.Image loadingStyle={ index > 0 ? 'lazy' : 'eager'} />
								</ProductCard.Header>
								<ProductCard.Content>
									<div className="product-card-content-main">
										<ProductCard.Title />
										<ProductCard.Meta />
										<ProductCard.Brand />
									</div>
									<div className="product-card-footer">
										<ProductCard.Pricing />
										<ProductCard.AddToCart />
									</div>
									<ProductCard.Slideout />
								</ProductCard.Content>
							</ProductCard>
						</SwiperSlide>
					))
				: null}
			</Swiper>
			<button className="btn-reset product-slider-prev" ref={prevBtnRef} aria-label="Previous Slide">
				<PrevIcon />
			</button>
			<button className="btn-reset product-slider-next" ref={nextBtnRef} aria-label="Next Slide">
				<NextIcon />
			</button>
		</div>
		</>
	);

	if ( portal ) {
		return createPortal(
			content,
			target
		);
	} else {
		return (
			content
		);
	}
}
export default ProductSlider;